import { Controller } from "@hotwired/stimulus"
import {useIntersection} from 'stimulus-use'
import consumer from "../channels/consumer"
import { sanitizeHtml } from "../helpers/sanitize_html"


export default class extends Controller {
    static values = {
        participants: Array
    }
    static targets = ["messageWindow", "readMessage", "lastMessage", "emptyState"];

    connect() {
        useIntersection(this)

        this.boundResizePanel = this.resizePanel.bind(this)
        window.addEventListener("resize", this.boundResizePanel)
        window.addEventListener("orientationchange", this.boundResizePanel)
        window.visualViewport?.addEventListener("resize", this.boundResizePanel)
        window.visualViewport?.addEventListener("scroll", this.boundResizePanel)

        if (window.ResizeObserver && this.element.parentElement) {
            this.panelResizeObserver = new ResizeObserver(this.boundResizePanel)
            this.panelResizeObserver.observe(this.element.parentElement)
        }

        this.subscription = consumer.subscriptions.create(
            {
                channel: "ChatChannel",
                id: this.data.get("id"),
            },
            {
                connected: this._connected.bind(this),
                disconnected: this._disconnected.bind(this),
                received: this._received.bind(this)
            }
        );
        this.showReadStatusAll()

        window.requestAnimationFrame(() => {
            this.resizePanel()
            window.requestAnimationFrame(() => this.scrollDown())
        })

    }

    disconnect() {
        window.removeEventListener("resize", this.boundResizePanel)
        window.removeEventListener("orientationchange", this.boundResizePanel)
        window.visualViewport?.removeEventListener("resize", this.boundResizePanel)
        window.visualViewport?.removeEventListener("scroll", this.boundResizePanel)
        this.panelResizeObserver?.disconnect()

        if (this.subscription) {
            consumer.subscriptions.remove(this.subscription)
            this.subscription = null
        }
    }

    _connected() {

    }

    _disconnected() {

    }

    _received(data) {
        if (data.event === "message.created" || data.action === "add") {
            this.add_message(data)
        }
        if (data.event === "chat.read" || data.action === "update") {
            this.setReadStatus(data)
        }

    }

    add_message(data) {
        const incomingMessage = this.data.get("user") !== data.chat_user
        this.upsertParticipant(data.participant)
        if (this.hasEmptyStateTarget) this.emptyStateTarget.remove()
        const last_message_el = this.hasLastMessageTarget ? this.lastMessageTarget : null
        last_message_el?.remove();
        if (this.data.get("user") == data.chat_user) {
            this.messageWindowTarget.insertAdjacentHTML("beforeend", sanitizeHtml(data.my_message_html.replaceAll('~', '')));
        } else {
            this.messageWindowTarget.insertAdjacentHTML("beforeend", sanitizeHtml(data.other_message_html))
        }
        if (last_message_el) {
            last_message_el.dataset.messageId = data.message?.id || ""
            this.messageWindowTarget.appendChild(last_message_el);
        }
        this.showReadStatusAll()
        window.requestAnimationFrame(() => {
            this.scrollDown()

            if (incomingMessage) {
                window.requestAnimationFrame(() => this.acknowledgeVisibleMessage())
            }
        })

    }

    scrollDown() {
        if (!this.hasMessageWindowTarget) return
        this.messageWindowTarget.scrollTop = this.messageWindowTarget.scrollHeight
    }

    resizePanel() {
        const keepAtBottom = this.isNearBottom()
        const viewport = window.visualViewport
        const viewportTop = viewport?.offsetTop || 0
        const viewportHeight = viewport?.height || window.innerHeight
        const viewportBottom = viewportTop + viewportHeight
        const panelTop = this.element.getBoundingClientRect().top
        const availableHeight = Math.max(0, Math.floor(viewportBottom - panelTop))
        const height = `${availableHeight}px`

        if (this.element.style.height !== height) {
            this.element.style.height = height
            this.element.style.maxHeight = height
        }

        if (keepAtBottom) {
            window.requestAnimationFrame(() => this.scrollDown())
        }
    }

    isNearBottom() {
        if (!this.hasMessageWindowTarget) return true

        const messageWindow = this.messageWindowTarget
        return messageWindow.scrollHeight - messageWindow.scrollTop - messageWindow.clientHeight <= 24
    }

    submitEndScroll(event) {
        if (event?.detail && !event.detail.success) return

        window.requestAnimationFrame(() => {
            this.resizePanel()
            this.scrollDown()
        })
    }

    tabChangeScroll() {
        this.resizePanel()
        window.requestAnimationFrame(() => this.scrollDown())

    }

    appear(entry) {
        this.tabChangeScroll();

    }

    readIcon(p) {
        const color = p.color || "bg-slate-500"
        const initials = (p.name || "Read").split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()
        const icon = document.createElement("span")
        icon.className = `inline-flex h-4 w-4 -space-x-1 items-center justify-center rounded-full ${color} text-[8px] font-semibold text-white ring-1 ring-white`
        icon.textContent = initials
        icon.title = `Read by ${p.name || "participant"}`
        icon.setAttribute("aria-label", icon.title)
        return icon
    }

    setReadStatus(data) {
        this.upsertParticipant({
            ...data.participant,
            last_read_message_id: data.last_read_message_id || data.participant?.last_read_message_id
        })
        this.showReadStatusAll()
    }

    showReadStatus(message) {
        const messageId = Number(message.dataset.messageId)
        const icons = []
        this.participantsValue.forEach((participant) => {
            const hasRead = Number(participant.last_read_message_id) >= messageId
            const isSender = participant.id === message.dataset.messageUser
            const isViewer = participant.id === this.data.get("user")

            if (hasRead && !isSender && !isViewer) icons.push(this.readIcon(participant))
        })
        message.replaceChildren(...icons)
    }


    showReadStatusAll() {
        this.readMessageTargets.forEach(this.showReadStatus, this)
    }

    broadcastReadLastMessage() {
        if (!this.hasLastMessageTarget) return

        this.subscription.perform("mark_read", { message_id: this.lastMessageTarget.dataset.messageId })
    }

    acknowledgeVisibleMessage() {
        if (document.visibilityState !== "visible") return
        if (this.element.getClientRects().length === 0) return

        this.broadcastReadLastMessage()
    }

    upsertParticipant(participant) {
        if (!participant?.id) return

        const participants = [...this.participantsValue]
        const index = participants.findIndex((item) => item.id === participant.id)
        if (index === -1) {
            participants.push(participant)
        } else {
            participants[index] = { ...participants[index], ...participant }
        }
        this.participantsValue = participants
    }

}
